import { api } from '../api/client'
import type { PriceHistory, PriceQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions } from '../AppLayout'
import { useListParams } from '../hooks/useListParams'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { EmptyState, ErrorBox, Note } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { InstrumentLink, betrag, geld, stueck, tag }
  from './priceFormat'

const PERMISSION = 'price_history:read'
const PAGE_SIZE = 50

const COLUMNS: Column<PriceHistory>[] = [
  { key: 'name', label: 'Instrument', sortable: true, minWidth: '200px',
    render: (row) => <InstrumentLink id={row.instrument_id} name={row.name} /> },
  { key: 'isin', label: 'ISIN', sortable: true, mono: true, copy: true,
    minWidth: '190px', hideOn: 'tablet' },
  { key: 'exchange', label: 'Platz', sortable: true, mono: true,
    minWidth: '110px', hideOn: 'mobile' },
  { key: 'trading_date', label: 'Handelstag', sortable: true, mono: true,
    minWidth: '130px',
    render: (row) => tag.format(new Date(row.trading_date)) },
  { key: 'open', label: 'Eröffnung', mono: true, align: 'right',
    minWidth: '110px', hideOn: 'tablet',
    render: (row) => betrag(row.open.amount) },
  { key: 'high', label: 'Hoch', mono: true, align: 'right', minWidth: '110px',
    hideOn: 'tablet', render: (row) => betrag(row.high.amount) },
  { key: 'low', label: 'Tief', mono: true, align: 'right', minWidth: '110px',
    hideOn: 'tablet', render: (row) => betrag(row.low.amount) },
  { key: 'close', label: 'Schluss', sortable: true, mono: true, align: 'right',
    // NUR HIER die Umrechnung, nicht in allen fuenf Spalten: Fuenfmal
    // dieselbe Waehrung untereinander waere Laerm. Der Schlusskurs ist der
    // Wert, mit dem gerechnet wird — an ihm haengt die Frage.
    minWidth: '150px', copy: (row) => row.close.amount,
    render: (row) => geld(row.close),
    help: 'Schlusskurs des Handelstags. Grundlage für Indikatoren wie RSI und EMA — dafür gilt dieser Wert, nicht der bereinigte.' },
  { key: 'adj_close', label: 'Bereinigt', mono: true, align: 'right',
    minWidth: '120px', hideOn: 'mobile',
    render: (row) => betrag(row.adj_close.amount),
    help: 'Um Dividenden und Splits bereinigter Schlusskurs. Er weicht RÜCKWIRKEND vom Schlusskurs ab und ist die Grundlage für Renditevergleiche — nicht für Indikatoren.' },
  { key: 'volume', label: 'Volumen', sortable: true, mono: true, align: 'right',
    minWidth: '130px', hideOn: 'mobile',
    render: (row) => stueck(row.volume) },
  // Die Waehrung bleibt als eigene Spalte: Sie gilt fuer alle fuenf Werte
  // der Zeile, und sie in jeden einzelnen zu schreiben waere Wiederholung.
  { key: 'currency', label: 'Währung', mono: true, minWidth: '100px',
    hideOn: 'tablet', render: (row) => row.close.currency },
]

export function PriceHistoryPage() {
  const allowed = useHasPermission(PERMISSION)

  const list = useListParams({
    // `currency` steht NICHT dabei — sie hängt an der Sitzung, nicht an
    // dieser Liste. Siehe `useZielwaehrung`.
    filters: { q: '', exchange: '', since: '', until: '' },
    sort: 'trading_date',
    descending: true,
  })

  // Die moeglichen Zielwaehrungen — aus den vorhandenen Kursen.
  const { waehrung, wahl } = useZielwaehrung(allowed)

  // In der Kopfzeile, nicht in der Filterleiste: Die Zielwaehrung aendert
  // die Darstellung, nicht die Menge der Zeilen.
  usePageActions(wahl)

  const params: PriceQuery = {
    q: list.filters.q.trim() || undefined,
    exchange: list.filters.exchange.trim().toUpperCase() || undefined,
    currency: waehrung ? [waehrung] : undefined,
    // Hier stehen echte Handelstage, keine Zeitpunkte — das Datum geht
    // unverändert durch, und der Zeitraum ist beidseitig einschließend.
    since: list.filters.since || undefined,
    until: list.filters.until || undefined,
    sort: list.sort,
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
  }

  const { result, loading, error, denied } = useListQuery(
    api.priceHistory, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Kurshistorie" />
  }

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Name oder Symbol — mindestens zwei Zeichen"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Input label="Handelsplatz" placeholder="z. B. NASDAQ"
                 value={list.filters.exchange}
                 onChange={(event) =>
                   list.setFilter('exchange', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.since}
                 onChange={(event) =>
                   list.setFilter('since', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.until}
                 onChange={(event) =>
                   list.setFilter('until', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          loading={loading && result === null}
          empty={<EmptyState title="keine Handelstage">
            {list.touched
              ? 'Kein Handelstag passt zu diesen Filtern.'
              : 'Es ist noch keine Kurshistorie erfasst.'}
          </EmptyState>}
        />
      </Card>

      <Note>
        Elf Spalten: Auf schmalen Bildschirmen fallen ISIN, Eröffnung, Hoch,
        Tief und Währung weg, unter 768 px auch Platz, Bereinigt und Volumen.
        Der Rest scrollt seitlich, das Instrument bleibt stehen.
      </Note>
    </>
  )
}
